import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import React from 'react';
import { useTenantApi } from '../hooks/useTenantApi';
import { Button } from './ui/button';

interface PartnerResourceModalProps {
  resource: {
    externalId: string;
    displayName?: string;
  };
  isOpen: boolean;
  onClose: () => void;
  schemaDefinitionId: string;
}

export const PartnerResourceModal: React.FC<PartnerResourceModalProps> = ({
  resource,
  isOpen,
  onClose,
  schemaDefinitionId,
}) => {
  const tenantApi = useTenantApi();
  const {
    data: partnerResource,
    isLoading,
    error,
  } = useQuery({
    queryKey: tenantApi.queryKey('resource', schemaDefinitionId, resource.externalId),
    queryFn: () =>
      tenantApi.findResourceByExternalId({ schemaDefinitionId, externalId: resource.externalId }),
    enabled: Boolean(
      isOpen && resource.externalId && schemaDefinitionId && schemaDefinitionId.trim() !== '',
    ),
    retry: false, // Don't retry if the resource is not found
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Resource Details</h2>
              <p className="text-sm text-gray-500 mt-1">External ID: {resource.externalId}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="ml-2 text-sm text-gray-500">Loading resource details...</span>
              </div>
            ) : error ? (
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-red-800">Error loading resource</h3>
                <div className="mt-2 text-sm text-red-700">
                  {error instanceof Error ? error.message : 'Resource not found'}
                </div>
              </div>
            ) : partnerResource ? (
              <div className="space-y-6">
                {/* Resource Data */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Resource Data</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(partnerResource, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <h3 className="text-sm font-semibold text-gray-900">No Resource Found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No partner resource was found for this user's external ID ({resource.externalId}). This
                  user may not have any associated partner resources yet.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
